// Curated, hand-picked Unsplash photos used as fallbacks when the
// brand crawler hasn't captured a real cover for a page yet. Stable
// IDs (not the `source.unsplash.com/random` API) so the same slug
// always renders the same image, no surprise swaps on rebuild.
//
// Each photo is grouped by category. unsplashPlaceholderFor(slug,
// category?) picks one deterministically by slug-hash.

export type PlaceholderCategory =
  | 'fashion'
  | 'beauty'
  | 'jewellery'
  | 'home'
  | 'furniture'
  | 'hospitality'
  | 'electronics'
  | 'sport'
  | 'b2b'
  | 'ugc'
  | 'commerce'
  | 'editorial'
  | 'baby'
  | 'pet'

const photos: Record<PlaceholderCategory, string[]> = {
  fashion: [
    // Verified Unsplash IDs (last validated 2026-05-12). Pool kept
    // large (24) so any deterministic slug lands on a unique image
    // even when multiple components draw from the same pool.
    'photo-1485125639709-a60c3a500bf1',
    'photo-1483985988355-763728e1935b',
    'photo-1539109136881-3be0616acf4b',
    'photo-1469334031218-e382a71b716b',
    'photo-1542060748-10c28b62716f',
    'photo-1551232864-3f0890e580d9',
    'photo-1495121605193-b116b5b9c5fe',
    'photo-1496747611176-843222e1e57c',
    'photo-1551489186-cf8726f514f8',
    'photo-1492447166138-50c3889fccb1',
    'photo-1469398715555-76331a6c7c9b',
    'photo-1525507119028-ed4c629a60a3',
    'photo-1520975916090-3105956dac38',
    'photo-1556905055-8f358a7a47b2',
    'photo-1554412933-514a83d2f3c8',
    'photo-1583744946564-b52ac1c389c8',
    'photo-1572804013309-59a88b7e92f1',
    'photo-1490481651871-ab68de25d43d',
    'photo-1515886657613-9f3515b0c78f',
    'photo-1509631179647-0177331693ae',
    'photo-1581338834647-b0fb40704e21',
    'photo-1576566588028-4147f3842f27',
    'photo-1542295669297-4d352b042bca',
    'photo-1487222477894-8943e31ef7b2',
  ],
  beauty: [
    'photo-1487412947147-5cebf100ffc2',
    'photo-1556228720-195a672e8a03',
    'photo-1556228720-195a672e8a03',
    'photo-1571781926291-c477ebfd024b',
    'photo-1631730486572-226d1f595b68',
    'photo-1596462502278-27bfdc403348',
    'photo-1512496015851-a90fb38ba796',
    'photo-1503236823255-94609f598e71',
    'photo-1457972729786-0411a3b2b626',
    'photo-1531895861208-8504b98fe814',
  ],
  jewellery: [
    // 8 hand-picked jewellery shots so a 6-tile dedupe row never falls
    // through to the generic ALL pool and shows wrong-category models.
    'photo-1611591437281-460bfbe1220a', // rings stack
    'photo-1617038220319-276d3cfab638', // chain on neck
    'photo-1599643478518-a784e5dc4c8f', // earrings closeup
    'photo-1600003263720-95b45a4035d5', // necklace styled
    'photo-1605100804763-247f67b3557e', // bracelet stack
    'photo-1515562141207-7a88fb7ce338', // pendant on chain
    'photo-1535632787350-4e68ef0ac584', // pearl earrings
    'photo-1573408301185-9146fe634ad0', // engagement ring closeup
  ],
  home: [
    'photo-1505691938895-1758d7feb511',
    'photo-1556909114-f6e7ad7d3136',
    'photo-1493663284031-b7e3aefcae8e',
    'photo-1567538096630-e0c55bd6374c',
    'photo-1600121848594-d8644e57abab',
    'photo-1586023492125-27b2c045efd7',
  ],
  furniture: [
    'photo-1540574163026-643ea20ade25',
    'photo-1567538096630-e0c55bd6374c',
    'photo-1555041469-a586c61ea9bc',
    'photo-1538688525198-9b88f6f53126',
    'photo-1592078615290-033ee584e267',
    'photo-1493663284031-b7e3aefcae8e',
    'photo-1565538810643-b5bdb714032a',
    'photo-1505693416388-ac5ce068fe85',
  ],
  hospitality: [
    'photo-1566073771259-6a8506099945',
    'photo-1582719508461-905c673771fd',
    'photo-1551882547-ff40c63fe5fa',
    'photo-1455587734955-081b22074882',
    'photo-1564501049412-61c2a3083791',
  ],
  electronics: [
    'photo-1505740420928-5e560c06d30e',
    'photo-1518770660439-4636190af475',
    'photo-1519389950473-47ba0277781c',
    'photo-1496181133206-80ce9b88a853',
    'photo-1517336714731-489689fd1ca8',
  ],
  sport: [
    'photo-1517649763962-0c623066013b',
    'photo-1571902943202-507ec2618e8f',
    'photo-1551698618-1dfe5d97d256',
    'photo-1530549387789-4c1017266635',
    'photo-1541534741688-6078c6bfb5c5',
  ],
  b2b: [
    'photo-1521737604893-d14cc237f11d',
    'photo-1552664730-d307ca884978',
    'photo-1573164713988-8665fc963095',
    'photo-1556761175-5973dc0f32e7',
    'photo-1542744173-8e7e53415bb0',
  ],
  ugc: [
    'photo-1611605698335-8b1569810432',
    'photo-1521119989659-a83eee488004',
    'photo-1564585222527-c2777a5bc6cb',
    'photo-1587825140708-dfaf72ae4b04',
    'photo-1567784177951-6fa58317e16b',
  ],
  commerce: [
    'photo-1607082348824-0a96f2a4b9da',
    'photo-1556742044-3c52d6e88c62',
    'photo-1472851294608-062f824d29cc',
    'photo-1556761175-b413da4baf72',
    'photo-1607082350899-7e105aa886ae',
  ],
  editorial: [
    'photo-1499951360447-b19be8fe80f5',
    'photo-1454165804606-c3d57bc86b40',
    'photo-1497515114629-f71d768fd07c',
    'photo-1493612276216-ee3925520721',
    'photo-1542435503-956c469947f6',
  ],
  baby: [
    // Hand-pinned, validated (last checked 2026-05-13). Used for the
    // /industries/baby-and-kids page so the Collect/Tag/Embed mocks
    // don't fall back to generic laptop / lightbulb editorial shots.
    'photo-1492725764893-90b379c2b6e7', // baby being fed
    'photo-1519689680058-324335c77eba', // baby sleeping
    'photo-1531123897727-8f129e1688ce', // parent + baby
    'photo-1544126592-807ade215a0b',    // baby on linen
    'photo-1518894664619-15a83b6c0d2c',
    'photo-1503944583220-79d8926ad5e2',
    'photo-1555252333-9f8e92e65df9',
    'photo-1607461194611-58c5e62e6ce4',
  ],
  pet: [
    'photo-1546975490-e8b92a360b24', // person + dog walking
    'photo-1583337130417-3346a1be7dee', // dog being fed
    'photo-1601758228041-f3b2795255f1', // golden retriever portrait
    'photo-1583511655857-d19b40a7a54e', // puppy
    'photo-1551717743-49959800b1f6',    // husky outdoors
    'photo-1574144611937-0df059b5ef3e', // cat on bed
    'photo-1450778869180-41d0601e046e',
    'photo-1517423440428-a5a00ad493e8',
  ],
}

const ALL: string[] = Object.values(photos).flat()

function hash(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

// Return a stable Unsplash image URL for a slug. Optionally constrain
// to a category. Default photo size 1200×800; pass width/height for a
// different crop.
export function unsplashPlaceholderFor(
  slug: string,
  options: { category?: PlaceholderCategory; width?: number; height?: number } = {},
): string {
  const { category, width = 1200, height = 800 } = options
  const pool = category && photos[category] ? photos[category] : ALL
  const id = pool[hash(slug) % pool.length]
  return `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${width}&h=${height}&q=80`
}

// Pull `count` images for a given category, deterministically seeded
// but with NO duplicates within the returned list. Used by gallery
// hero rows that look broken when the same Unsplash shot repeats.
export function uniquePlaceholderRow(
  seed: string,
  count: number,
  category: PlaceholderCategory,
  width = 260,
  height = 340,
): string[] {
  const pool = photos[category] || ALL
  const seen = new Set<string>()
  const out: string[] = []
  // Try each pool offset starting from a hashed start index. If a
  // category pool is too small, fall through to ALL so we don't loop
  // forever or silently return dups.
  const candidates = pool.length >= count ? pool : ALL
  const start = hash(seed) % candidates.length
  for (let step = 0; step < candidates.length && out.length < count; step++) {
    const id = candidates[(start + step) % candidates.length]
    if (seen.has(id)) continue
    seen.add(id)
    out.push(`https://images.unsplash.com/${id}?auto=format&fit=crop&w=${width}&h=${height}&q=80`)
  }
  return out
}

// Assign a unique placeholder image to every item in a list. Each
// item keeps its own category where possible; once a category pool is
// exhausted the assignment falls through to the full pool, so no two
// items in the same list ever render the same photo (which reads as
// duplicate content). Order-stable for a given input list.
export function uniquePlaceholdersFor(
  items: { slug: string; category?: PlaceholderCategory }[],
  options: { width?: number; height?: number } = {},
): string[] {
  const { width = 1200, height = 800 } = options
  const used = new Set<string>()
  const pick = (pool: string[], seed: string): string | undefined => {
    const start = hash(seed) % pool.length
    for (let step = 0; step < pool.length; step++) {
      const candidate = pool[(start + step) % pool.length]
      if (!used.has(candidate)) return candidate
    }
    return undefined
  }
  return items.map((item) => {
    const pool = item.category && photos[item.category] ? photos[item.category] : ALL
    const id = pick(pool, item.slug) ?? pick(ALL, item.slug) ?? pool[hash(item.slug) % pool.length]
    used.add(id)
    return `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${width}&h=${height}&q=80`
  })
}

// Heuristic, derive a category from a slug or title using simple
// keyword matches. Useful for blog cards where we don't know the
// category up-front.
export function guessCategoryFromText(input: string): PlaceholderCategory {
  const t = input.toLowerCase()
  if (/(fashion|apparel|outfit|cloth|shirt|denim|wear)/.test(t))           return 'fashion'
  if (/(beauty|skin|makeup|cosm|lipstick|spf)/.test(t))                    return 'beauty'
  if (/(jewel|jewell|ring|necklace|earring|bracelet)/.test(t))             return 'jewellery'
  if (/(home|interior|decor|kitchen|bedroom|living)/.test(t))              return 'home'
  if (/(furniture|sofa|chair|table|bed|lamp)/.test(t))                     return 'furniture'
  if (/(hotel|hospitality|resort|spa|stay|travel)/.test(t))                return 'hospitality'
  if (/(electronic|gadget|tech|laptop|phone|camera)/.test(t))              return 'electronics'
  if (/(sport|fitness|run|gym|outdoor|trail|cycling)/.test(t))             return 'sport'
  if (/(baby|kid|infant|toddler|nursery|parent)/.test(t))                  return 'baby'
  if (/(pet|dog|cat|puppy|kitten|animal)/.test(t))                         return 'pet'
  if (/(b2b|saas|enterprise|api|developer|integration)/.test(t))           return 'b2b'
  if (/(ugc|user[- ]generated|creator|hashtag|customer photo)/.test(t))    return 'ugc'
  if (/(shopify|woo|commerce|checkout|cart|review)/.test(t))               return 'commerce'
  return 'editorial'
}
